import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { NotificationDataService } from '@/core/NotificationManager/NotificationDataService';
import { NotificationToastService } from '@/core/NotificationManager/NotificationToastService';
import { debugLogger } from '@/utils/debugLogger';
import { toast } from 'sonner';

const NOTIFICATIONS_ENABLED_KEY = 'notifications_enabled';

export interface SimpleNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  category: string;
  is_read: boolean;
  created_at: string;
  source_id: string | null;
  user_avatar?: string;
  username?: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<SimpleNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRealtimeActive, setIsRealtimeActive] = useState(false);
  const [notificationsEnabled, setNotificationsEnabledState] = useState<boolean>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(NOTIFICATIONS_ENABLED_KEY);
    return stored !== null ? stored === 'true' : true;
  });
  const channelRef = useRef<any>(null);
  const processedIdsRef = useRef<Set<string>>(new Set());

  // Persist notificationsEnabled to localStorage
  const setNotificationsEnabled = (enabled: boolean) => {
    setNotificationsEnabledState(enabled);
    localStorage.setItem(NOTIFICATIONS_ENABLED_KEY, enabled.toString());
    debugLogger.debug('notifications', 'Notifications enabled changed:', enabled);
  };

  // Fetch notifications function (exposed for pull-to-refresh)
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { notifications: data, unreadCount: count } = 
        await NotificationDataService.fetchNotifications(user.id);
      
      setNotifications(data);
      setUnreadCount(count);
      
      // Mark all fetched as processed to avoid duplicate toasts
      data.forEach(n => processedIdsRef.current.add(n.id));
      
      debugLogger.debug('notifications', '✅ Fetch completed', {
        count: data.length,
        unread: count
      });
    } catch (error) {
      debugLogger.error('notifications', '❌ Fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Refetch function for pull-to-refresh
  const refetch = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // STEP 1: Fetch synchronously on mount
  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const initialFetch = async () => {
      try {
        setLoading(true);
        const { notifications: data, unreadCount: count } = 
          await NotificationDataService.fetchNotifications(user.id);
        
        if (isMounted) {
          setNotifications(data);
          setUnreadCount(count);
          
          // Mark all fetched as processed to avoid duplicate toasts
          data.forEach(n => processedIdsRef.current.add(n.id));
          
          debugLogger.debug('notifications', '✅ Sync fetch completed', {
            count: data.length,
            unread: count
          });
        }
      } catch (error) {
        debugLogger.error('notifications', '❌ Sync fetch failed:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initialFetch();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  // STEP 2: Connect to realtime AFTER sync data is shown
  useEffect(() => {
    if (!user?.id || loading) return;

    debugLogger.debug('notifications', '🔌 Connecting to realtime...');

    const channel = supabase
      .channel(`notifications_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const newNotification = payload.new as any;

          // Avoid duplicate processing
          if (processedIdsRef.current.has(newNotification.id)) {
            debugLogger.debug('notifications', 'Duplicate INSERT ignored:', newNotification.id);
            return;
          }

          processedIdsRef.current.add(newNotification.id);

          debugLogger.debug('notifications', '📩 New notification via realtime:', newNotification.id);

          const enriched = newNotification as SimpleNotification;

          // Update state
          setNotifications(prev => [enriched, ...prev]);
          if (!enriched.is_read) {
            setUnreadCount(prev => prev + 1);
          }

          // Show toast only if notifications are enabled
          if (notificationsEnabled) {
            NotificationToastService.showToast(enriched);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const deletedId = payload.old.id;

          debugLogger.debug('notifications', '🗑️ Notification deleted via realtime:', deletedId);

          setNotifications(prev => {
            const wasUnread = prev.find(n => n.id === deletedId)?.is_read === false;
            if (wasUnread) {
              setUnreadCount(count => Math.max(0, count - 1));
            }
            return prev.filter(n => n.id !== deletedId);
          });

          processedIdsRef.current.delete(deletedId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updated = payload.new as SimpleNotification;

          debugLogger.debug('notifications', '✏️ Notification updated via realtime:', updated.id);

          setNotifications(prev => prev.map(n => 
            n.id === updated.id ? { ...n, ...updated } : n
          ));
          
          // Recalculate unread count
          setNotifications(prev => {
            const newUnread = prev.filter(n => !n.is_read).length;
            setUnreadCount(newUnread);
            return prev;
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsRealtimeActive(true);
          debugLogger.debug('notifications', '✅ Realtime connected successfully');
        } else if (status === 'CHANNEL_ERROR') {
          setIsRealtimeActive(false);
          debugLogger.debug('notifications', 'ℹ️ Realtime connection failed, using sync data only');
        }
      });

    channelRef.current = channel;

    return () => {
      debugLogger.debug('notifications', '🔌 Disconnecting from realtime');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsRealtimeActive(false);
    };
  }, [user?.id, loading, notificationsEnabled]);

  // Actions
  const removeNotification = async (notificationId: string) => {
    if (!user?.id) return false;

    try {
      // Optimistic update
      const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      const success = await NotificationDataService.removeNotification(notificationId);
      
      if (!success) {
        // Revert on error
        toast.error('Erro ao remover notificação');
        // Refetch to restore state
        const { notifications: data, unreadCount: count } = 
          await NotificationDataService.fetchNotifications(user.id);
        setNotifications(data);
        setUnreadCount(count);
      }

      return success;
    } catch (error) {
      toast.error('Erro ao remover notificação');
      return false;
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!user?.id) return false;

    try {
      // Optimistic update
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      const success = await NotificationDataService.markAsRead(notificationId);
      
      if (!success) {
        toast.error('Erro ao marcar como lida');
        const { notifications: data, unreadCount: count } = 
          await NotificationDataService.fetchNotifications(user.id);
        setNotifications(data);
        setUnreadCount(count);
      }

      return success;
    } catch (error) {
      toast.error('Erro ao marcar como lida');
      return false;
    }
  };

  /**
   * Marks all notifications as read (does NOT delete them)
   */
  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      // Optimistic update - mark all as read
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);

      await NotificationDataService.markAllAsRead();
      toast.success('Todas as notificações marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações como lidas');
      const { notifications: data, unreadCount: count } = 
        await NotificationDataService.fetchNotifications(user.id);
      setNotifications(data);
      setUnreadCount(count);
    }
  };

  /**
   * Deletes all notifications permanently
   */
  const deleteAllNotifications = async () => {
    if (!user?.id) return;

    try {
      // Optimistic update - clear all
      setNotifications([]);
      setUnreadCount(0);

      await NotificationDataService.deleteAllNotifications();
      toast.success('Todas as notificações foram apagadas');
    } catch (error) {
      toast.error('Erro ao apagar notificações');
      const { notifications: data, unreadCount: count } = 
        await NotificationDataService.fetchNotifications(user.id);
      setNotifications(data);
      setUnreadCount(count);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    isRealtimeActive,
    notificationsEnabled,
    setNotificationsEnabled,
    removeNotification,
    markAsRead,
    markAllAsRead,
    deleteAllNotifications,
    refetch
  };
}
